import { getAuth } from 'firebase/auth';
import { environment } from '../config/environment';
import { useApiClient } from './api';
import type { CheckoutSessionRequest, CheckoutSessionResponse, UserPlans } from './payment.types';

/**
 * Cello validation response from our backend / Cello API
 */
export interface CelloValidationResponse {
    success: boolean;
    data: {
      code: string;
      productUserId: string;
      valid: boolean;
      campaignId: string; 
      productUserDetails: {
        firstName: string;
        lastName: string;
        fullName: string;
        email: string;
      };
      error?: string; // Detailed error message for debugging
    }
}

/**
 * Service for payment-related API calls
 */
export const usePaymentService = () => {
    const apiClient = useApiClient();

    /**
     * Create a checkout session for payment
     * @param request Checkout session request data
     * @returns Promise with checkout session response
     */
    const createCheckoutSession = async (request: CheckoutSessionRequest): Promise<CheckoutSessionResponse> => {
        try {
            // Get current user email
            const auth = getAuth();
            const currentUser = auth.currentUser;
            const userEmail = currentUser ? currentUser.email : null;

            // Get Cello UCC
            let celloUcc = null;
            try {
                if (typeof window !== 'undefined' && (window as any).CelloAttribution) {
                    celloUcc = await (window as any).CelloAttribution('getUcc');
                    console.log('[CELLO] Retrieved UCC for checkout:', celloUcc);
                } else {
                    console.log('[CELLO] CelloAttribution not available on window or window is undefined.');
                }
            } catch (error) {
                console.error('[CELLO] Error retrieving UCC:', error);
            }

            const response = await apiClient.post<CheckoutSessionResponse>(
                `${environment.expressApiUrl}/api/payment/create-checkout-session`,
                {
                    ...request,
                    userEmail,
                    celloUcc, // Add Cello UCC to the payload
                }
            );
            console.log('[PaymentService] createCheckoutSession - raw apiClient response:', response);

            if (response && response.data && response.data.id && response.data.url) {
                const sessionId = response.data.id;
                const sessionUrl = response.data.url;

                // Return the original session ID and URL, plus new fields
                return {
                    id: sessionId,
                    url: sessionUrl
                };
            } else {
                console.error('Checkout session creation response missing id or url:', response);
                throw new Error('Failed to retrieve complete checkout session details from the server.');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            throw error;
        }
    };

    /**
     * Get user plans
     * @returns Promise with user plans data
     */
    const getUserPlans = async (): Promise<UserPlans> => {
        try {
            const response = await apiClient.get(`${environment.expressApiUrl}/api/payment/user-plans`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user plans:', error);
            throw error;
        }
    };

    /**
     * Create a refund for a payment
     * @param paymentIntentId Payment intent ID to refund
     * @param reason Reason for the refund
     * @returns Promise with refund result
     */
    const createRefund = async (paymentIntentId: string, reason?: string) => {
        try {
            const response = await apiClient.post(`${environment.expressApiUrl}/api/payment/refund`, {
                paymentIntentId,
                reason,
            });
            return response.data;
        } catch (error) {
            console.error('Error creating refund:', error);
            throw error;
        }
    };

    /**
     * Get refund status
     * @param refundId Refund ID to check
     * @returns Promise with refund status
     */
    const getRefundStatus = async (refundId: string) => {
        try {
            const response = await apiClient.get(`${environment.expressApiUrl}/api/payment/refund/${refundId}`);
            return response.data;
        } catch (error) {
            console.error('Error getting refund status:', error);
            throw error;
        }
    };

    /**
     * Create a billing portal session
     * @returns Promise with billing portal URL
     */
    const createBillingPortalSession = async () => {
        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                throw new Error('User not logged in');
            }

            const token = await currentUser.getIdToken();
            const response = await apiClient.post(
                `${environment.expressApiUrl}/api/payment/create-billing-portal-session`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating billing portal session:', error);
            throw error;
        }
    };

    /**
     * Validate Cello Unique Campaign Code (UCC)
     * @param ucc Cello UCC to validate
     * @returns Validation result from the backend, structured as CelloValidationResponse
     */
    const validateCelloUcc = async (ucc: string): Promise<CelloValidationResponse | null> => {
        if (!ucc) {
            console.log('[CELLO] validateCelloUcc received falsy ucc, returning null.');
            return null;
        }

        try {
            // Our backend endpoint /api/cello/validate-ucc/:ucc is expected to return CelloValidationResponse structure
            const response = await apiClient.get<CelloValidationResponse>(`${environment.expressApiUrl}/api/cello/validate-ucc/${ucc}`);
            // Assuming backend response.data directly matches CelloValidationResponse
            console.log('[CELLO] validateCelloUcc successful response.data:', response.data);
            return response.data;
        } catch (error: unknown) {
            console.error('[CELLO] Error validating UCC via backend:', error);
            // Attempt to parse error response from backend if it also follows CelloValidationResponse { success, data } structure
            if (error && typeof error === 'object' && 'response' in error &&
                error.response && typeof error.response === 'object' && 'data' in error.response &&
                (error.response as any).data && typeof (error.response as any).data.success === 'boolean' &&
                (error.response as any).data.data && typeof (error.response as any).data.data.valid === 'boolean') {
                console.log('[CELLO] validateCelloUcc returning structured error.response.data:', (error.response as any).data);
                return (error.response as any).data as CelloValidationResponse;
            }
            // Fallback for network errors or unexpected response structures, matching CelloValidationResponse { success, data }
            const fallbackResponse = {
                success: false,
                data: {
                    code: ucc,
                    productUserId: '',
                    valid: false,
                    campaignId: '',
                    productUserDetails: {
                        firstName: '',
                        lastName: '',
                        fullName: '',
                        email: ''
                    },
                    error: error instanceof Error ? error.message : 'Failed to validate UCC',
                }
            };
            console.log('[CELLO] validateCelloUcc returning structured fallbackResponse:', fallbackResponse);
            return fallbackResponse;
        }
    };

    /**
     * Get invoice or credit note PDF by payment ID
     * @param paymentId Stripe payment intent ID or charge ID for refunds
     * @param isRefund Whether this is a refund document
     * @returns Promise with document URL
     */
    const getInvoice = async (paymentId: string, isRefund: boolean = false): Promise<{ invoiceUrl: string; isReceipt: boolean }> => {
        try {
            const endpoint = isRefund 
                ? `${environment.expressApiUrl}/api/payment/credit-note/${paymentId}` 
                : `${environment.expressApiUrl}/api/payment/invoice/${paymentId}`;
            const response = await apiClient.get(endpoint);
            return response.data;
        } catch (error) {
            console.error('Error fetching invoice/credit note:', error);
            throw error;
        }
    };
    
    /**
     * Get all invoices and receipts for the current user
     * @returns Promise with all invoices
     */
    const getAllInvoices = async (): Promise<{
        invoices: Array<{
            id: string;
            paymentIntentId: string;
            chargeId: string;
            amount: number;
            status: string;
            currency: string;
            invoiceUrl?: string;
            receiptUrl?: string;
            date: string;
            description: string;
            isRefund: boolean;
            metadata: Record<string, any>;
        }>;
    }> => {
        try {
            const response = await apiClient.get(`${environment.expressApiUrl}/api/payment/invoices`);
            return response.data;
        } catch (error) {
            console.error('Error fetching all invoices:', error);
            throw error;
        }
    };

    /**
     * Get custom pricing for individual payment link
     * @returns Promise with custom pricing data
     */
    const getIndividualPaymentPricing = async (): Promise<{
        weDoEverything: {
            price: number;
            currency: string;
            description: string;
            transactions: number;
        };
    }> => {
        try {
            const response = await apiClient.get(`${environment.expressApiUrl}/api/payment/individual-payment-pricing`);
            return response.data;
        } catch (error) {
            console.error('Error fetching individual payment pricing:', error);
            throw error;
        }
    };

    return {
        createCheckoutSession,
        getUserPlans,
        createRefund,
        getRefundStatus,
        createBillingPortalSession,
        getInvoice,
        getAllInvoices,
        validateCelloUcc,
        getIndividualPaymentPricing,
    };
};
